import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateFAQSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function Contact() {
  const faqSchema = generateFAQSchema([
    {
      question: 'What areas do you serve?',
      answer: 'We provide non-emergency medical transportation services throughout Orange County and surrounding areas in Southern California. Contact us to confirm service availability in your specific location.'
    },
    {
      question: 'How far in advance should I book?',
      answer: 'We recommend booking at least 24-48 hours in advance for scheduled appointments. However, we also accommodate same-day requests based on availability. For emergency transport, we\'re available 24/7.'
    },
    {
      question: 'Do you accept insurance?',
      answer: 'Yes, we work with most major insurance providers including Medicare, Medi-Cal, and private insurance companies. Contact us to verify your specific coverage and benefits.'
    },
    {
      question: 'Are your drivers trained and certified?',
      answer: 'Absolutely. All our drivers are professionally trained, certified, and undergo regular background checks. They are experienced in patient care and equipped to handle various mobility needs safely and compassionately.'
    },
    {
      question: 'What types of vehicles do you use?',
      answer: 'Our fleet includes wheelchair-accessible vans, ambulatory vehicles, and gurney-equipped transport units. All vehicles are regularly maintained, sanitized, and equipped with safety features to ensure comfortable and secure transportation.'
    }
  ]);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Contact', url: '/contact' }
  ]);

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [faqSchema, breadcrumbSchema]
  };

  useSEO({
    title: 'Contact InteCare Westminster CA - Medical Transportation Booking | (714) 798-7318',
    description: 'Contact InteCare for non-emergency medical transportation in Westminster CA and Orange County. Call (714) 798-7318 or email intecare.nemt@gmail.com. 24/7 service available with Integrity and Care.',
    keywords: 'contact InteCare, book medical transport Westminster, NEMT booking Orange County, medical transportation phone number, schedule medical transport',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
    schema: combinedSchema
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate textarea length
    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4trcqh2tio6iafo07kg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-50 to-emerald-50 py-32 overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/eb80b406b1aec82901caff26562713ec.jpg"
              alt="InteCare Contact Background"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/75 to-white/85"></div>
          </div>

          {/* Content */}
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                Get In Touch
              </h1>
              <p className="text-xl text-gray-600 mb-2">
                We're here to help with all your non-emergency medical transportation needs. Reach out to us anytime.
              </p>
              <p className="text-lg text-teal-600 italic">
                Serving you with Integrity and Care
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information Cards */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Phone */}
              <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl border border-teal-100 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-phone-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Call Us</h3>
                <p className="text-gray-600 mb-3">Available 24/7 services</p>
                <a href="tel:+17147987318" className="text-teal-600 hover:text-teal-700 font-semibold text-lg">
                  (714) 798-7318
                </a>
              </div>

              {/* Email */}
              <div className="bg-gradient-to-br from-emerald-50 to-white p-8 rounded-2xl border border-emerald-100 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Email Us</h3>
                <p className="text-gray-600 mb-3">We'll respond within 24 hours</p>
                <a href="mailto:intecare.nemt@gmail.com" className="text-emerald-600 hover:text-emerald-700 font-semibold">
                  intecare.nemt@gmail.com
                </a>
              </div>

              {/* Location */}
              <div className="bg-gradient-to-br from-cyan-50 to-white p-8 rounded-2xl border border-cyan-100 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Visit Us</h3>
                <p className="text-gray-600 mb-3">Our office location</p>
                <p className="text-cyan-600 font-medium">
                  8512 Westminster Blvd Ste B<br />
                  Westminster, CA 92683
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form & Map */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                  Send Us a Message
                </h2>
                <p className="text-gray-600 mb-8">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>

                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="(714) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    >
                      <option value="">Select a subject</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Service Information">Service Information</option>
                      <option value="Booking Question">Booking Question</option>
                      <option value="Feedback">Feedback</option>
                      <option value="Partnership">Partnership Opportunity</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message * (Max 500 characters)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="Tell us how we can help you..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.message.length}/500 characters
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                      <p className="font-medium">Thank you for contacting us!</p>
                      <p className="text-sm">We'll get back to you within 24 hours.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                      <p className="font-medium">Oops! Something went wrong.</p>
                      <p className="text-sm">Please try again or call us directly.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>

              {/* Map & Business Hours */}
              <div className="space-y-8">
                {/* Map */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
                    Find Us Here
                  </h3>
                  <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200" style={{ height: '400px' }}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3316.8234567890123!2d-117.9939!3d33.7513!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDQ1JzA0LjciTiAxMTfCsDU5JzM4LjAiVw!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus&q=8512+Westminster+Blvd+Ste+B,+Westminster,+CA+92683"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="InteCare NEMT Location"
                    ></iframe>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl border border-teal-100">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                    Business Hours
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-teal-100">
                      <span className="font-semibold text-gray-700">Monday - Friday</span>
                      <span className="text-gray-600">6:00 AM - 8:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-teal-100">
                      <span className="font-semibold text-gray-700">Saturday - Sunday</span>
                      <span className="text-gray-600">8:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">Transportation Services</span>
                      <span className="text-teal-600 font-semibold">24/7 Available</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-teal-100">
                    <p className="text-sm text-gray-600 mb-4">
                      Need immediate assistance? Call us now!
                    </p>
                    <a
                      href="tel:+17147987318"
                      className="flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      (714) 798-7318
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center" style={{ fontFamily: '"Playfair Display", serif' }}>
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 text-center mb-12">
                Quick answers to common questions about our services
              </p>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    What areas do you serve?
                  </h4>
                  <p className="text-gray-600">
                    We provide non-emergency medical transportation services throughout Orange County and surrounding areas in Southern California. Contact us to confirm service availability in your specific location.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    How far in advance should I book?
                  </h4>
                  <p className="text-gray-600">
                    We recommend booking at least 24-48 hours in advance for scheduled appointments. However, we also accommodate same-day requests based on availability. For emergency transport, we're available 24/7.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    Do you accept insurance?
                  </h4>
                  <p className="text-gray-600">
                    Yes, we work with most major insurance providers including Medicare, Medi-Cal, and private insurance companies. Contact us to verify your specific coverage and benefits.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    Are your drivers trained and certified?
                  </h4>
                  <p className="text-gray-600">
                    Absolutely. All our drivers are professionally trained, certified, and undergo regular background checks. They are experienced in patient care and equipped to handle various mobility needs safely and compassionately.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    What types of vehicles do you use?
                  </h4>
                  <p className="text-gray-600">
                    Our fleet includes wheelchair-accessible vans, ambulatory vehicles, and gurney-equipped transport units. All vehicles are regularly maintained, sanitized, and equipped with safety features to ensure comfortable and secure transportation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
